import React from 'react';
import {Helmet} from 'react-helmet';
import {getLocationUrl} from '../molecules/filter';

export interface MetaTagsProps {
  content?: string;
  lang: string;
  title: string;
  description: string;
  keywords: (string | null)[];
  ogImage?: string;
}

const assetPrefix =
  ['staging', 'production', 'prod'].indexOf(process.env.GATSBY_DEPLOY_ENV || '') > -1 ? 'new-website/' : '';

const capitalize = (str: string) =>
  str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const createBreadcrumbSchema = (urlStr: string) => {
  const url = new URL(urlStr);
  const previousPaths = url.pathname.split('/').filter(path => path !== '');
  let currentUrl = url.origin;

  const breadcrumbItems = previousPaths.map((path, i) => {
    currentUrl += `/${path}`;

    return {
      '@type': 'ListItem',
      position: 2 + i, // Index starts from 2 since the first index is always homepage
      name: capitalize(path.replace(/-/g, ' ')),
      item: currentUrl
    };
  });

  return breadcrumbItems;
};

export const MetaTags: React.FC<MetaTagsProps> = ({content, title, description, keywords, ogImage, lang}) => {
  const ogImageContent = ogImage
    ? ogImage.replace(/^(https?:)?\/{2,}/i, 'https://')
    : `https://front.com/${assetPrefix}og_image.png`;

  const url = getLocationUrl();
  let urlObj;
  let schema;

  if (url) {
    urlObj = new URL(url);
    schema = createBreadcrumbSchema(urlObj.href);
  }

  return (
    <Helmet
      htmlAttributes={{
        lang
      }}
      title={title}
      meta={[
        {
          property: `og:title`,
          content: title
        },
        {
          name: `description`,
          content: description
        },
        {
          property: `og:description`,
          content: description
        },
        {
          name: `image`,
          property: `og:image`,
          content: ogImageContent
        },
        {
          property: 'og:type',
          content: content || 'article'
        },
        {
          name: `keywords`,
          content: keywords ? keywords.filter(keyword => keyword).join(`, `) : ''
        },
        {
          name: 'google-site-verification',
          content: '3c54pSQHXyIkcaYN7IugzXRAl6rbf_O9QQT-kMBHZ3w'
        },
        {
          name: 'msapplication-config',
          content: `/${assetPrefix}browserconfig.xml`
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image'
        },
        {
          name: 'twitter:site',
          content: '@frontapp'
        },
        {
          name: 'twitter:creator',
          content: '@frontapp'
        },
        {
          name: 'twitter:title',
          content: title
        },
        {
          name: 'twitter:description',
          content: description
        },
        {
          name: 'twitter:image',
          content: ogImageContent
        }
      ]}
      link={[
        {
          rel: 'manifest',
          href: `/${assetPrefix}site.webmanifest`
        },
        {
          rel: 'icon',
          type: 'image/x-icon',
          href: `/${assetPrefix}icons/favicon.ico`
        },
        {
          rel: 'icon',
          type: 'image/png',
          href: `/${assetPrefix}icons/favicon-16x16.png`
        },
        {
          rel: 'icon',
          type: 'image/png',
          href: `/${assetPrefix}icons/favicon-32x32.png`
        },
        {
          rel: 'apple-touch-icon',
          sizes: '180x180',
          href: `/${assetPrefix}icons/apple-touch-icon.png`
        },
        {
          rel: 'mask-icon',
          color: '#ff0c64',
          href: `/${assetPrefix}icons/safari-pinned-tab.svg`
        }
      ]}
      script={[
        urlObj
          ? {
            type: 'application/ld+json',
            innerHTML: `{
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "${urlObj.origin}"
                }${schema && schema.length > 0 ? ',' : ''}
                ${schema?.map(item => JSON.stringify(item, null, 2))}
              ]
            }`
          }
          : undefined
      ].filter(Boolean)}
    />
  );
};
